/**
 * Map Chip DOM Component
 *
 * Renders a single map chip button.
 * No Redux, no business logic — receives all values as parameters.
 *
 * Structure:
 *   <button ..__chip [--active]>
 *     <span>Available</span>
 *     <span ..__chip-badge>26</span>
 *   </button>
 *
 * @module nativeInteractiveSiteMap/components/mapChip.dom
 */

import fastdom from 'fastdom';

const BUTTON_CLASS = 'nhs-c-native-interactive-site-map__chip';
const BUTTON_ACTIVE_CLASS = `${BUTTON_CLASS}--active`;
const BUTTON_DISABLED_CLASS = `${BUTTON_CLASS}--disabled`;
const BADGE_CLASS = `${BUTTON_CLASS}-badge`;

/**
 * @param {object} params
 * @param {string}  params.label    - Display text for the chip (e.g. 'Coming Soon')
 * @param {number}  params.count    - Initial count to display in the badge
 * @param {string}  params.chipKey  - Store/filter value identifying this chip (e.g. 'coming-soon')
 * @param {boolean} params.isActive - Whether this chip is initially active
 * @returns {{ getElement: Function, setActive: Function, 
 *             setDisabled: Function, setCount: Function, 
 *             setVisible: Function, remove: Function 
 *          }}
 */
export default function createMapChipDom({ label, count, chipKey, isActive }) {
  const state = { root: null };

  function getBadge() {
    return state.root?.querySelector('[data-chip-count]');
  }

  function initializeDom() {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
    <button
      type="button"
      class="${BUTTON_CLASS}${isActive ? ` ${BUTTON_ACTIVE_CLASS}` : ''}"
      data-filter="${chipKey}"
      aria-pressed="${isActive ? 'true' : 'false'}"
      data-qa="native_ism_mapchip_${chipKey}">
      <span>${label}</span>
      <span class="${BADGE_CLASS}" data-chip-count data-qa="native_ism_mapchip_${chipKey}Number">${count}</span>
    </button>`;
    state.root = wrapper.firstElementChild;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    setActive(active) {
      fastdom.mutate(() => {
        state.root?.classList.toggle(BUTTON_ACTIVE_CLASS, active);
        state.root?.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    },

    /**
     * Disables the chip entirely when it has no matching lots.
     * Adds the --disabled class for styling and sets the disabled HTML attribute
     * to prevent browser-native interactions.
     * @param {boolean} isDisabled
     */
    setDisabled(isDisabled) {
      fastdom.mutate(() => {
        state.root?.classList.toggle(BUTTON_DISABLED_CLASS, isDisabled);
        state.root?.toggleAttribute('disabled', isDisabled);
      });
    },

    setCount(newCount) {
      fastdom.mutate(() => {
        const badge = getBadge();
        if (badge) {
          badge.textContent = newCount;
        }
      });
    },

    /**
     * Hides the chip entirely (not just visually) when it has nothing to offer — a chip with
     * zero matching lots adds no value. Uses the `hidden` attribute rather than removing the
     * element so setActive/setCount keep working if it becomes visible again later.
     * @param {boolean} isVisible
     */
    setVisible(isVisible) {
      fastdom.mutate(() => {
        state.root?.toggleAttribute('hidden', !isVisible);
      });
    },

    remove() {
      const root = state.root;
      state.root = null;
      fastdom.mutate(() => { root?.remove(); });
    },
  };
}
